import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-row"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "flex-1" }, null, -1)
const _hoisted_3 = {
  key: 1,
  class: "mb-12"
}
const _hoisted_4 = { class: "text-gray-900 font-bold text-2xl" }
const _hoisted_5 = {
  key: 0,
  class: "mt-4 text-success-500"
}
const _hoisted_6 = {
  key: 1,
  class: "mt-4 text-danger-500"
}

import { computed, onBeforeMount, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormConfig, FormItem, FormSection, ManagementPlan, ManagementPlanStep, Patient } from '@/models';
import { EpisodeOfCareEncounter, EpisodeOfCareResponseData } from '@/models/episode-of-care/episode-of-care.model';
import { CodeableConceptService, PatientFormSubmissionService } from '@/services/api';
import { CodeableConcept, IOption } from '@/lib';
import { BaseIcon } from '@/lib/components/Icon';
import CopyableTextSummary from '@/lib/components/CopyableTextSummary.vue';
import { getDobFromISOString } from '@/helpers/patient.helper';
import { codeableConceptCategories } from '@/constants';
import { useSmartFormStore } from '@/stores/smartForm.store';
import { useNotificationStore } from '@/stores/notification.store';
import axios from 'axios';


export default /*#__PURE__*/_defineComponent({
  __name: 'ReviewSummary',
  props: {
  patient: {
    type: Object as PropType<Patient>,
    required: true
  },
  episodeOfCare: {
    type: Object as PropType<EpisodeOfCareResponseData>,
    required: true
  },
  showHeader: {
    type: Boolean,
    default: true
  },
  organisationId: {
    type: String,
    required: true
  }
},
  emits: ["copy", "close"],
  setup(__props, { emit: __emit }) {

dayjs.extend(utc);

const { t, d } = useI18n();
const props = __props;
const emit = __emit;

const virtualEncounter = ref<EpisodeOfCareEncounter | null>(null);
const managementPlan = ref<ManagementPlan | null>(null);
const managementPlanNote = ref('');
const loading = ref(false);
const formSubmissionService = new PatientFormSubmissionService(props.patient.id);
const codeableConceptService = new CodeableConceptService();
const participantTypes = ref<CodeableConcept[]>([]);
const reviewCode = ref<CodeableConcept | null>(null);
const ambulatoryEncounter = ref<EpisodeOfCareEncounter | null>(null);
const formSubmissionId = ref<string | null>(null);
const schema = ref<FormConfig>({
  id: '',
  name: '',
  sections: [],
  completion: []
});

const success = ref(false);
const error = ref(false);
const smartForm = useSmartFormStore();
const notificationStore = useNotificationStore();

const patientInfos = computed(() => props.patient
  ? {
    [t(
      'platform.common.name'
    ) as string]: `${props.patient.first_name} ${props.patient.last_name}`,
    [t('platform.patient.dob') as string]: formatDob(
      props.patient.date_of_birth
    ),
    MRN: props.patient.mrn ?? (props.episodeOfCare.ophthalmology_details.clinic.owner?.id === props.organisationId ?
      props.episodeOfCare.patient_mrn_at_clinic_owner : props.episodeOfCare.patient_mrn_at_clinic_provider)
  }
  : {});
const clinicSummary = computed(() => {
  const reviewerCode = findCodeableConceptByCode('REV', participantTypes.value);
  const reviewer = virtualEncounter.value?.participants.find(
    (participant) => participant.type_id === reviewerCode?.id
  );
  const clinicAddress = props.episodeOfCare.ophthalmology_details.clinic.provider?.address?.formatted_address;
  const clinic = props.episodeOfCare.ophthalmology_details.clinic.name;
  const dateCompleted = ambulatoryEncounter.value ? formatDate(
    ambulatoryEncounter.value.statuses.filter((status) => status.end === null)[0].start
  ) : '';
  return {
    [t('custom.uhb.consult.clinic') as string]: clinic,
    [t('custom.uhb.review.date-completed') as string]: dateCompleted,
    [t('custom.uhb.consult.clinician') as string]: reviewer?.user?.name,
    ...(clinicAddress ? { [t('platform.patient.location') as string]: clinicAddress } : {})
  };
});
const findCodeableConceptByCode = (code: string, codeableConcepts: CodeableConcept[]) =>
  codeableConcepts.find((codableConcept) => codableConcept.code === code);
const completedDate = computed(() => {
  const completeAt = virtualEncounter.value?.end ? virtualEncounter.value?.end :
    virtualEncounter.value?.statuses.find((status) => status.status_id === reviewCode.value?.id)?.end;
  return {
    [t('custom.uhb.review.date-completed') as string]: completeAt ? formatDate(completeAt) :
      t('custom.uhb.review.not-complete-date')
  };
});
const formatContext = computed(() => {
  let result = '';
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  loading.value = true;
  // Patient Info
  result += formatSummary(
    t('custom.uhb.review.summary.patient-summary') as string,
    patientInfos.value
  );
  if (virtualEncounter.value) {
    // Clinic Summary
    result += formatSummary(
      t('custom.uhb.review.summary.clinic-summary') as string,
      clinicSummary.value
    );

    // Review Summary
    result += formatSummary(
      t('custom.uhb.review.summary.summary') as string,
      completedDate.value
    );
  }

  // Form
  if (schema.value) {
    result += formatForm();
  }
  // Management Plan
  if (managementPlan.value) {
    result += formatManagementPlan();
  }
  // Export date
  result += `<br><br>Export generated on : ${today.value} via Big Picture Platform`;
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  loading.value = false;
  return result;
});
const today = computed(() => `${dayjs.utc().format('DD MMM YYYY, HH:mm:ss')}(UTC)`);

const getCodeableConceptByCategory = async (category: string): Promise<CodeableConcept[]> => (
  await codeableConceptService.index({
    params: {
      'filter[category]': category
    }
  })
).data;
const fetchCodeableConceptsByCategory = async (category: string, errorMessage: string) => {
  let codeableConcepts;
  try {
    loading.value = true;
    codeableConcepts = await getCodeableConceptByCategory(category);
  } catch (error) {
    if (!axios.isAxiosError(error)) {
      throw error;
    }
    await notificationStore.addErrorNotification({
      title: errorMessage,
      label: error.response.data?.message || ''
    });
  } finally {
    loading.value = false;
  }

  return codeableConcepts;
};
const fetchEncounterByClass = (classId?: string): EpisodeOfCareEncounter | undefined =>
  props.episodeOfCare.encounters.find((e) => e.class === classId);
onBeforeMount(async () => {
  try {
    const encounterClasses = await fetchCodeableConceptsByCategory(
      codeableConceptCategories.encounterClass,
      t('custom.uhb.episode-of-care.encounter-class-fetch-error')
    );
    const encounterStatusCodes = await fetchCodeableConceptsByCategory(
      codeableConceptCategories.encounterStatus,
      t('custom.uhb.episode-of-care.encounter-class-fetch-error')
    );
    participantTypes.value = await fetchCodeableConceptsByCategory(
      codeableConceptCategories.participantType,
      t('custom.uhb.episode-of-care.participant-type-fetch-error')
    );

    reviewCode.value = findCodeableConceptByCode('in-progress', encounterStatusCodes);
    const ambulatoryClassCode = findCodeableConceptByCode('AMB', encounterClasses);
    ambulatoryEncounter.value = fetchEncounterByClass(ambulatoryClassCode?.id);
    formSubmissionId.value = ambulatoryEncounter.value?.form_submission_id ?? '';

    // Set the virtual encounter
    const virtualClassCode = findCodeableConceptByCode('VR', encounterClasses);
    virtualEncounter.value = fetchEncounterByClass(virtualClassCode?.id);

    syncManagementPlan();
    await fetchFormSubmission();
  } catch (e) {
    await notificationStore.addErrorNotification({
      title: t('platform.error.fetch-data')
    });
  }
});
const syncManagementPlan = () => {
  if (virtualEncounter.value?.plan) {
    managementPlan.value = virtualEncounter.value.plan;
    if (managementPlan.value) {
      managementPlanNote.value = managementPlan.value.review_summary_markdown;
    }
  }
};
const fetchFormSubmission = async () => {
  const formSubmission = await formSubmissionService.fetch(formSubmissionId.value);
  if (formSubmission.form?.schema) {
    schema.value = formSubmission.form.schema;
    smartForm.answers = {
      ...smartForm.answers,
      [formSubmissionId.value]: formSubmission.answers
    };
  }
};
const conditionsMet = (section: FormSection | FormItem) => smartForm.getItemConditionsMet(
  formSubmissionId.value,
  section.conditions
);
const formatDob = (isoDate: string) => d(getDobFromISOString(isoDate), 'short');
const formatSummary = (title: string, params: { [key: string]: any }) => {
  let result = '';
  result += formatTitle(title);
  if (title && params) {
    for (const [key, value] of Object.entries(params)) {
      result += `<br>${key}: ${value}`;
    }
  }
  return result;
};
const formatForm = () => {
  let result = '';
  if (schema.value.sections.length > 0) {
    result += formatTitle(
      `${schema.value.name} (${t('platform.form.type')})`
    );
    for (let i = 0; i < schema.value.sections.length; i++) {
      if (
        conditionsMet(schema.value.sections[i]) &&
        schema.value.sections[i].heading &&
        !schema.value.sections[i].hide_when_readonly
      ) {
        result += formatSection(schema.value.sections[i]);
      }
    }
  }
  return result;
};
const formatSection = (section: FormSection) => {
  let result = '';
  if (section.heading) {
    result += formatTitle(section.heading);
  }
  if (section.items) {
    result += formatItems(section.items);
  }
  return result;
};
const formatItems = (items: Array<FormItem>) => {
  let result = '';
  for (let i = 0; i < items.length; i++) {
    result += formatItem(items[i]);
  }
  return result;
};
const formatItem = (item: FormItem) => {
  let result = '';
  if (conditionsMet(item) && item.type !== 'hidden' && item.label) {
    if (
      item.type === 'select' ||
      item.type === 'radio' ||
      item.type === 'checkbox' ||
      item.type === 'slider'
    ) {
      if (item.type === 'checkbox' || item.type === 'radio') {
        // Multiselect use array for answer.
        result += `${formatItemLabel(item.label)}: ${answer(
          item.id
        )}`;
      } else if (item.type === 'slider' && item.props) {
        // BaseSlider use array index for answer.
        result += `${formatItemLabel(item.label)}: ${
          item.props.options[answer(item.id)]
        }`;
      } else {
        // BaseSelect use option id for answer.
        const optionId = answer(item.id);
        const option =
          item.props &&
          item.props.options.find(
            (option: IOption) => option.value === optionId
          );
        result += `${formatItemLabel(item.label)}: ${
          option ? option.label : t('platform.common.not-answered')
        }`;
      }
    } else if (item.type === 'repeater') {
      // Loop the repeater group and display all the answers
      result += formatRepeaterQuestions(item);
    } else if (item.type === 'group') {
      // If type is group, just display label.
      result += formatItemLabel(item.label);
    } else if (item.type === 'component') {
      result += formatMedication(item);
    } else {
      result += `${formatItemLabel(item.label)}: ${answer(
        item.id
      )}`;
    }
  }

  if (item.type === 'list-item') {
    result += formatListItem(item);
  }

  if (item.items && item.type !== 'repeater') {
    result += formatItems(item.items);
  }
  return result;
};
const formatManagementPlan = () => {
  let result = '';
  if (managementPlan.value && managementPlan.value.actions.length > 0) {
    for (let i = 0; i < managementPlan.value.actions.length; i++) {
      if (virtualEncounter.value?.extension?.unable_to_diagnose) {
        result += formatTitle(
          t('custom.uhb.diagnosis.unable') as string
        );
        if (virtualEncounter.value.technical_issues?.length) {
          result += '<br>Technical issues: ';
          for (let j = 0; j < virtualEncounter.value.technical_issues.length; j++) {
            result += `<br>${virtualEncounter.value.technical_issues[j].name}`;
          }
        }
      }
      const diagnoses = managementPlan.value.actions[i].diagnoses;
      const steps = managementPlan.value.actions[i].steps;

      if (diagnoses.length > 0) {
        result += formatTitle(
          t('custom.uhb.diagnosis.diagnosis') as string
        );
        for (let j = 0; j < diagnoses.length; j++) {
          result += `<br>${diagnoses[j].anatomy.name}`;
          result += `<br> ${
            diagnoses[j].is_present
              ? diagnoses[j].condition.name
              : t('custom.uhb.diagnosis.no-evidence-of', [
                diagnoses[j].condition.name
              ])
          }`;
          result += diagnoses[j].stage
            ? ` (${diagnoses[j].stage?.name || ''}) `
            : '';
          result += diagnoses[j].status
            ? `[${diagnoses[j].status?.name || ''}]`
            : '';
        }
      }
      result +=
        steps && steps.length ? formatManagementPlanSteps(steps) : '';
    }
  }

  if (managementPlanNote.value) {
    result += formatTitle(
      t('custom.uhb.management.notes') as string
    );
    result += `<br>${managementPlanNote.value}<br>`;
  }
  return result;
};
const formatTitle = (title: string) => `<br><br><b>${title}</b>`;
const formatItemLabel = (label: string) => `<br><br>${label}`;
const formatManagementPlanSteps = (steps: Array<ManagementPlanStep>) => {
  let result = formatTitle(
    t('custom.uhb.management.plan') as string
  );
  for (let i = 0; i < steps.length; i++) {
    result += `<br>${formatManagementPlanStep(steps[i])}`;
  }
  return result;
};
const formatManagementPlanStep = (step: ManagementPlanStep) => {
  const timeframe =
    step.timeframe.name === 'None'
      ? `[${t('platform.common.none')}] `
      : `[${step.timeframe.name}] `;
  const notes = step.notes
    ? ` (${t('platform.common.notes')}:${step.notes})`
    : '';
  return timeframe + step.destination.name + notes;
};
const formatRepeaterQuestions = (item: FormItem) => {
  let result = '';
  const repeaterAnswers = answer(item.id);
  if (typeof repeaterAnswers === 'string' && item.items) {
    for (let i = 0; i < item.items.length; i++) {
      if (item.items[i].label) {
        result += `<br><br>${item.items[i].label}: ${repeaterAnswers}`;
      } else if (
        repeaterAnswers !== t('platform.common.not-answered')
      ) {
        result += `<br><br>${repeaterAnswers}`;
      }
    }
  } else {
    for (let i = 0; i < Object.keys(repeaterAnswers).length; i++) {
      const repeaterAnswer = repeaterAnswers[i];
      const itemIdsOfAnswer = Object.keys(repeaterAnswer);
      for (let j = 0; j < itemIdsOfAnswer.length; j++) {
        const currentAnswerItemId = itemIdsOfAnswer[j];
        const itemOfRepeaterAnswer =
          item.items &&
          item.items.find(
            (item: FormItem) => item.id === currentAnswerItemId
          );
        if (itemOfRepeaterAnswer) {
          let label = itemOfRepeaterAnswer.label;
          if (itemOfRepeaterAnswer.type === 'date') {
            label = t('platform.common.date');
          }
          if (itemOfRepeaterAnswer.type === 'text') {
            label = t('custom.uhb.diagnosis.condition');
          }
          result += `<br><br>${label}: ${
            repeaterAnswers[i][currentAnswerItemId]?.value
          }`;
        }
      }
    }
  }
  return result.length ? `<br><br>${item.label}${result}` : '';
};
const formatMedication = (item: FormItem) => {
  let result = '';
  const answers = smartForm.getAnswer(
    formSubmissionId.value,
    item.id
  );
  if (!answers) {
    return t('platform.common.not-answered');
  }
  result += `<br><br>${item.label}`;
  for (let i = 0; i < Object.keys(answers).length; i++) {
    for (let j = 0; j < Object.keys(answers[i]).length; j++) {
      const answerKey = Object.keys(answers[i])[j];
      const answerValue = answers[i][Object.keys(answers[i])[j]];
      result += `<br>`;
      if (answerKey === 'medication') {
        result += `${t('platform.common.name')}: ${answerValue?.name?.value ?? t('platform.common.not-answered')}`;
      } else {
        result += `${answerKey}: ${answerValue?.value ?? t('platform.common.not-answered')}`;
      }
    }
  }
  return result;
};
const formatListItem = (item: FormItem) => {
  let result = '';

  const answers = smartForm.getAnswer(
    formSubmissionId.value,
    item.id
  );

  result += `<br>${item.props?.label}: ${answers[0]}`;

  if (!answers[0]) {
    result += ` - ${answers[1]}`;
  }

  return result;
};
const answer = (itemId: string) => {
  const answer = smartForm.getAnswer(
    formSubmissionId.value,
    itemId
  );

  if (typeof answer === 'undefined') {
    return t('platform.common.not-answered') as string;
  } else if (answer === true) {
    return t('platform.common.yes') as string;
  } else if (answer === false) {
    return t('platform.common.no') as string;
  }

  return answer;
};
const showSuccess = () => {
  success.value = true;
  error.value = false;
  emit('copy', true);
};
const showError = () => {
  success.value = false;
  error.value = true;
  emit('copy', false);
};
const formatDate = (date: string) => dayjs(date).format('DD MMM YYYY, [at] hh:mm:ss a');
const close = () => {
  emit('close');
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["bg-white", { 'p-16': __props.showHeader }])
  }, [
    (__props.showHeader)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _hoisted_2,
          _createElementVNode("button", {
            type: "button",
            onClick: close
          }, [
            _createVNode(_unref(BaseIcon), {
              class: "text-gray-400",
              name: "close"
            })
          ])
        ]))
      : _createCommentVNode("", true),
    (__props.showHeader)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("h1", _hoisted_4, _toDisplayString(_ctx.$t('custom.uhb.review.summary.summary')), 1),
          (success.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_ctx.$t('custom.uhb.review.summary.success-message')), 1))
            : _createCommentVNode("", true),
          (error.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_ctx.$t('custom.uhb.review.summary.error-message')), 1))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createVNode(CopyableTextSummary, {
      context: formatContext.value,
      loading: loading.value,
      onSuccess: showSuccess,
      onError: showError
    }, null, 8, ["context", "loading"])
  ], 2))
}
}

})